import FW from '../../fw/fw';
import Env from '../../fw/env';
import Logger from '../../fw/logger';

export default class ConfigureShakaPlayer {

  #rmp;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #prepConfig() {
    let configuration = {};
    configuration.abr = {};
    configuration.drm = {};
    configuration.manifest = {};
    configuration.restrictions = {};
    configuration.streaming = {};
    let restrictions = {};

    //consumeLessEnergy
    if (this.#rmp.consumeLessEnergy) {
      configuration.abr.bandwidthUpgradeTarget = 0.7;
      configuration.abr.bandwidthDowngradeTarget = 0.75;
      this.#rmp.capLevelToPlayerSize = true;
      this.#rmp.ignoreDevicePixelRatio = true;
      this.#rmp.shakaClearBufferSwitch = false;
      configuration.abr.preferNetworkInformationBandwidth = true;
    }

    // ABR
    configuration.abr.useNetworkInformation = true;

    if (this.#rmp.shakaClearBufferSwitch) {
      configuration.abr.clearBufferSwitch = true;
      configuration.abr.safeMarginSwitch = 12;
    }

    if (this.#rmp.capLevelToPlayerSize) {
      configuration.abr.restrictToElementSize = true;
    }
    configuration.abr.ignoreDevicePixelRatio = this.#rmp.ignoreDevicePixelRatio;
    // HardwareResolution
    configuration.ignoreHardwareResolution = this.#rmp.ignoreHardwareResolution;

    // preferred audioPreference lang - label - audioCodecs - spatialAudio
    if (typeof this.#rmp.audioPreference.lang === 'string' && this.#rmp.audioPreference.lang !== '') {
      configuration.preferredAudioLanguage = this.#rmp.audioPreference.lang;
    }
    if (typeof this.#rmp.audioPreference.label === 'string' && this.#rmp.audioPreference.label !== '') {
      configuration.preferredAudioLabel = this.#rmp.audioPreference.label;
    }
    if (Array.isArray(this.#rmp.audioPreference.audioCodecs) && this.#rmp.audioPreference.audioCodecs[0]) {
      configuration.preferredAudioCodecs = this.#rmp.audioPreference.audioCodecs;
    }
    if (this.#rmp.audioPreference.spatialAudio) {
      configuration.preferSpatialAudio = this.#rmp.audioPreference.spatialAudio;
    }

    // preferred subtitlePreference lang - label - textFormats
    if (typeof this.#rmp.subtitlePreference.lang === 'string' && this.#rmp.subtitlePreference.lang !== '') {
      configuration.preferredTextLanguage = this.#rmp.subtitlePreference.lang;
    }
    if (typeof this.#rmp.subtitlePreference.role === 'string' && this.#rmp.subtitlePreference.role !== '') {
      configuration.preferredTextRole = this.#rmp.subtitlePreference.role;
    }
    if (Array.isArray(this.#rmp.subtitlePreference.textFormats) && this.#rmp.subtitlePreference.textFormats[0]) {
      configuration.preferredTextFormats = this.#rmp.subtitlePreference.textFormats;
    }

    // preferred videoPreference label - videoHdr - videoCodecs
    if (typeof this.#rmp.videoPreference.label === 'string' && this.#rmp.videoPreference.label !== '') {
      configuration.preferredVideoLabel = this.#rmp.videoPreference.label;
    }
    if (Array.isArray(this.#rmp.videoPreference.videoCodecs) && this.#rmp.videoPreference.videoCodecs[0]) {
      configuration.preferredVideoCodecs = this.#rmp.videoPreference.videoCodecs;
    }

    if (typeof this.#rmp.videoPreference.videoHdr === 'string' && this.#rmp.videoPreference.videoHdr !== 'auto') {
      this.#rmp.videoPreference.videoHdr = this.#rmp.videoPreference.videoHdr.toUpperCase();
      if (['SDR', 'PQ', 'HLG'].includes(this.#rmp.videoPreference.videoHdr)) {
        configuration.preferredVideoHdrLevel = this.#rmp.videoPreference.videoHdr;
      }
    }

    // streaming
    configuration.streaming.bufferingGoal = this.#rmp.shakaMaxBufferAhead;
    configuration.streaming.bufferBehind = this.#rmp.shakaMaxBufferBehind;
    configuration.streaming.ignoreTextStreamFailures = true;
    if (this.#rmp.shakaLiveSync && Env.smartTVData.tv) {
      configuration.streaming.liveSync = this.#rmp.shakaLiveSync;
    }

    // configuration.manifest
    if (this.#rmp.disableAudio) {
      configuration.manifest.disableAudio = this.#rmp.disableAudio;
    }
    if (this.#rmp.disableVideo) {
      configuration.manifest.disableVideo = this.#rmp.disableVideo;
    }
    if (this.#rmp.disableText) {
      configuration.manifest.disableText = this.#rmp.disableText;
    }
    if (this.#rmp.disableThumbnails) {
      configuration.manifest.disableThumbnails = this.#rmp.disableThumbnails;
    }
    if (this.#rmp.disableIFrames) {
      configuration.manifest.disableIFrames = this.#rmp.disableIFrames;
    }

    // configuration.manifest.dash
    configuration.manifest.dash = {};
    if (!FW.isEmptyObject(this.#rmp.shakaKeySystemsByURI)) {
      configuration.manifest.dash.keySystemsByURI = this.#rmp.shakaKeySystemsByURI;
    }
    // retry parameters
    configuration.manifest.retryParameters = {
      maxAttempts: this.#rmp.retryParameters.manifest.maxAttempts,
      baseDelay: this.#rmp.retryParameters.manifest.delay,
    };
    configuration.streaming.retryParameters = {
      maxAttempts: this.#rmp.retryParameters.segment.maxAttempts,
      baseDelay: this.#rmp.retryParameters.segment.delay,
    };
    configuration.drm.retryParameters = {
      maxAttempts: this.#rmp.retryParameters.drm.maxAttempts,
      baseDelay: this.#rmp.retryParameters.drm.delay,
    };
    configuration.manifest.retryParameters.timeout = this.#rmp.retryParameters.manifest.timeout;
    configuration.streaming.retryParameters.timeout = this.#rmp.retryParameters.segment.timeout;
    configuration.drm.retryParameters.timeout = this.#rmp.retryParameters.drm.timeout;

    if (this.#rmp.lowLatencyMode) {
      configuration.streaming.lowLatencyMode = true;
      configuration.manifest.dash.autoCorrectDrift = true;
      configuration.manifest.retryParameters.baseDelay = 100;
      configuration.streaming.retryParameters.baseDelay = 100;
      configuration.drm.retryParameters.baseDelay = 100;
    }

    // drm 
    if (!FW.isEmptyObject(this.#rmp.shakaDrm)) {
      if (this.#rmp.shakaDrm.servers) {
        configuration.drm.servers = this.#rmp.shakaDrm.servers;
      }
      if (this.#rmp.shakaDrm.clearKeys) {
        configuration.drm.clearKeys = this.#rmp.shakaDrm.clearKeys;
      }
      if (this.#rmp.shakaDrm.advanced) {
        configuration.drm.advanced = this.#rmp.shakaDrm.advanced;
      }
      if (this.#rmp.preferredKeySystems.length > 0) {
        configuration.drm.preferredKeySystems = this.#rmp.preferredKeySystems;
      }
      if (this.#rmp.shakaDrm.minHdcpVersion !== '') {
        configuration.drm.minHdcpVersion = this.#rmp.shakaDrm.minHdcpVersion;
      }
    }
    // restrictions
    if (!FW.isEmptyObject(this.#rmp.shakaRestrictions)) {
      if (typeof this.#rmp.shakaRestrictions.minWidth === 'number') {
        restrictions.minWidth = this.#rmp.shakaRestrictions.minWidth;
      }
      if (typeof this.#rmp.shakaRestrictions.maxWidth === 'number') {
        restrictions.maxWidth = this.#rmp.shakaRestrictions.maxWidth;
      }
      if (typeof this.#rmp.shakaRestrictions.minHeight === 'number') {
        restrictions.minHeight = this.#rmp.shakaRestrictions.minHeight;
      }
      if (typeof this.#rmp.shakaRestrictions.maxHeight === 'number') {
        restrictions.maxHeight = this.#rmp.shakaRestrictions.maxHeight;
      }
      if (typeof this.#rmp.shakaRestrictions.minPixels === 'number') {
        restrictions.minPixels = this.#rmp.shakaRestrictions.minPixels;
      }
      if (typeof this.#rmp.shakaRestrictions.maxPixels === 'number') {
        restrictions.maxPixels = this.#rmp.shakaRestrictions.maxPixels;
      }
      if (typeof this.#rmp.shakaRestrictions.minAudioBandwidth === 'number') {
        restrictions.minAudioBandwidth = this.#rmp.shakaRestrictions.minAudioBandwidth;
      }
      if (typeof this.#rmp.shakaRestrictions.maxAudioBandwidth === 'number') {
        restrictions.maxAudioBandwidth = this.#rmp.shakaRestrictions.maxAudioBandwidth;
      }
      if (typeof this.#rmp.shakaRestrictions.minVideoBandwidth === 'number') {
        restrictions.minVideoBandwidth = this.#rmp.shakaRestrictions.minVideoBandwidth;
      }
      if (typeof this.#rmp.shakaRestrictions.maxVideoBandwidth === 'number') {
        restrictions.maxVideoBandwidth = this.#rmp.shakaRestrictions.maxVideoBandwidth;
      }
      if (!FW.isEmptyObject(restrictions)) {
        configuration.restrictions = restrictions;
      }
    }
    // preferredDecodingAttributes
    // 'smooth', 'powerEfficient', 'bandwidth'
    if (this.#rmp.consumeLessEnergy) {
      configuration.preferredDecodingAttributes = ['powerEfficient', 'bandwidth', 'smooth'];
    }
    // CMCD
    if (this.#rmp.enableCMCD) {
      configuration.cmcd = {
        enabled: true,
        version: this.#rmp.cmcdVersion,
        contentId: this.#rmp.cmcdContentId || FW.getCMCDContentId(this.#rmp.contentMetadata.id),
        sessionId: this.#rmp.cmcdSessionId || FW.generateUuid(),
        useHeaders: this.#rmp.useHeadersForCMCD
      };
      if (this.#rmp.cmcdIncludeKeys[0]) {
        configuration.cmcd.includeKeys = this.#rmp.cmcdIncludeKeys;
      }
    }
    // cmsd
    configuration.cmsd = {
      enabled: this.#rmp.enableCMSD
    };
    // delete unaffected
    if (FW.isEmptyObject(configuration.drm)) {
      delete configuration.drm;
    }
    if (FW.isEmptyObject(configuration.manifest)) {
      delete configuration.manifest;
    }
    if (FW.isEmptyObject(configuration.restrictions)) {
      delete configuration.restrictions;
    }
    return configuration;
  }

  init() {
    let configuration;
    if (!FW.isEmptyObject(this.#rmp.shakaFullCustomConfig)) {
      configuration = this.#rmp.shakaFullCustomConfig;
    } else {
      configuration = this.#prepConfig();
      if (!FW.isEmptyObject(this.#rmp.shakaCustomConfig)) {
        for (const [key, value] of Object.entries(this.#rmp.shakaCustomConfig)) {
          if (typeof value === 'object') {
            for (const [key1, value1] of Object.entries(value)) {
              configuration[key][key1] = value1;
            }
          } else {
            configuration[key] = value;
          }
        }
      }
    }
    this.#rmp.shakaPlayer.configure(configuration);
    Logger.print('shaka-player', 'initial configuration follows', this.#rmp.shakaPlayer.getConfiguration());

    // shakaCustomRequestFilter
    if (this.#rmp.shakaCustomRequestFilter !== null) {
      Logger.print('shaka-player', 'using shakaCustomRequestFilter', this.#rmp.shakaCustomRequestFilter);
      this.#rmp.shakaPlayer.getNetworkingEngine().registerRequestFilter(this.#rmp.shakaCustomRequestFilter);
    } else if (!FW.isEmptyObject(this.#rmp.shakaRequestConfiguration)) {
      Logger.print('shaka-player', 'using shakaRequestConfiguration', this.#rmp.shakaRequestConfiguration);
      let licenseHeaders = {};
      let licenseCredentials = false;
      let licenseParameters = '';
      if (this.#rmp.shakaRequestConfiguration.license) {
        if (this.#rmp.shakaRequestConfiguration.license.headers) {
          licenseHeaders = this.#rmp.shakaRequestConfiguration.license.headers;
        }
        if (typeof this.#rmp.shakaRequestConfiguration.license.credentials === 'boolean') {
          licenseCredentials = this.#rmp.shakaRequestConfiguration.license.credentials;
        }
        if (typeof this.#rmp.shakaRequestConfiguration.license.parameters === 'string') {
          licenseParameters = this.#rmp.shakaRequestConfiguration.license.parameters;
        }
      }
      let manifestCredentials = false;
      if (this.#rmp.shakaRequestConfiguration.manifest) {
        if (typeof this.#rmp.shakaRequestConfiguration.manifest.credentials === 'boolean') {
          manifestCredentials = this.#rmp.shakaRequestConfiguration.manifest.credentials;
        }
      }
      let segmentCredentials = false;
      if (this.#rmp.shakaRequestConfiguration.segment) {
        if (typeof this.#rmp.shakaRequestConfiguration.segment.credentials === 'boolean') {
          segmentCredentials = this.#rmp.shakaRequestConfiguration.segment.credentials;
        }
      }
      this.#rmp.shakaPlayer.getNetworkingEngine().registerRequestFilter((type, request) => {
        const RequestType = window.shaka.net.NetworkingEngine.RequestType;
        if (type === RequestType.LICENSE) {
          if (!FW.isEmptyObject(licenseHeaders)) {
            const keys = Object.keys(licenseHeaders);
            keys.forEach(key => {
              request.headers[key] = licenseHeaders[key];
            });
          }
          if (licenseParameters !== '') {
            request.uris[0] += licenseParameters;
          }
          if (licenseCredentials) {
            request.allowCrossSiteCredentials = true;
          }
        }
        if (type === RequestType.MANIFEST) {
          if (manifestCredentials) {
            request.allowCrossSiteCredentials = true;
          }
        }
        if (type === RequestType.SEGMENT) {
          if (segmentCredentials) {
            request.allowCrossSiteCredentials = true;
          }
        }
        Logger.print('shaka-player', 'registerRequestFilter - request:', request);
      });
    }

    if (this.#rmp.shakaCustomResponseFilter !== null) {
      Logger.print('shaka-player', 'using shakaCustomResponseFilter', this.#rmp.shakaCustomResponseFilter);
      this.#rmp.shakaPlayer.getNetworkingEngine().registerResponseFilter(this.#rmp.shakaCustomResponseFilter);
    }

    if (this.#rmp.shakaDrmFairPlayInitDataTransform !== null) {
      Logger.print('shaka-player', 'using shakaDrmFairPlayInitDataTransform', this.#rmp.shakaDrmFairPlayInitDataTransform);
      this.#rmp.shakaPlayer.configure('drm.initDataTransform', this.#rmp.shakaDrmFairPlayInitDataTransform);
    }
  }

}
